/* global axios */
import ApiClient from '../../ApiClient';

/**
 * WhatsApp Call REST API client.
 * Extends ApiClient to use account-scoped URLs and axios pattern
 * consistent with the rest of the codebase (e.g., whatsappChannel.js).
 */
class WhatsAppCallAPI extends ApiClient {
  constructor() {
    super('', { accountScoped: true });
  }

  inboxCallUrl(inboxId, action) {
    return `${this.baseUrl()}/inboxes/${inboxId}/whatsapp_calling/${action}`;
  }

  /**
   * Initiate an outbound call (B2C)
   * @param {number} inboxId
   * @param {number} contactId
   * @param {string} sdpOffer - WebRTC SDP offer from browser
   */
  initiateCall(inboxId, contactId, sdpOffer) {
    return axios.post(this.inboxCallUrl(inboxId, 'initiate'), {
      contact_id: contactId,
      sdp_offer: sdpOffer,
    });
  }

  /**
   * Accept an incoming call (C2B)
   * @param {number} inboxId
   * @param {number} conversationId - display_id of the conversation
   * @param {string} sdpAnswer - WebRTC SDP answer from browser
   */
  acceptCall(inboxId, conversationId, sdpAnswer) {
    return axios.post(this.inboxCallUrl(inboxId, 'accept'), {
      conversation_id: conversationId,
      sdp_answer: sdpAnswer,
    });
  }

  /**
   * Reject an incoming call
   * @param {number} inboxId
   * @param {number} conversationId
   */
  rejectCall(inboxId, conversationId) {
    return axios.post(this.inboxCallUrl(inboxId, 'reject'), {
      conversation_id: conversationId,
    });
  }

  /**
   * Terminate an active call
   * @param {number} inboxId
   * @param {number} conversationId
   */
  terminateCall(inboxId, conversationId) {
    return axios.post(this.inboxCallUrl(inboxId, 'terminate'), {
      conversation_id: conversationId,
    });
  }

  /**
   * Check call permission for a WhatsApp user
   * @param {number} inboxId
   * @param {string} userWaId - WhatsApp phone number
   */
  checkPermission(inboxId, userWaId) {
    return axios.get(this.inboxCallUrl(inboxId, 'check_permission'), {
      params: { user_wa_id: userWaId },
    });
  }

  /**
   * Request call permission from a WhatsApp user
   * @param {number} inboxId
   * @param {string} to - phone number
   * @param {string} text - optional custom message text
   */
  requestPermission(inboxId, to, text = null) {
    const payload = { to };
    if (text) payload.text = text;
    return axios.post(this.inboxCallUrl(inboxId, 'request_permission'), payload);
  }

  /**
   * Get current calling settings
   * @param {number} inboxId
   */
  getSettings(inboxId) {
    return axios.get(this.inboxCallUrl(inboxId, 'settings'));
  }

  /**
   * Enable calling for this inbox
   * @param {number} inboxId
   */
  enableCalling(inboxId) {
    return axios.post(this.inboxCallUrl(inboxId, 'enable'));
  }

  /**
   * Disable calling for this inbox
   * @param {number} inboxId
   */
  disableCalling(inboxId) {
    return axios.post(this.inboxCallUrl(inboxId, 'disable'));
  }
}

export default new WhatsAppCallAPI();
